import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';
import { BudgetMode, budgetModeOf } from '../../utility/budget';

// export interface BillingForecast {
//     summary: {
//         actual_spent_this_month: number;
//         forecasted_total_this_month: number;
//         forecasted_remaining: number;
//     };
//     by_service: {
//         service: string;
//         actual_cost_so_far: number;
//         current_hourly_rate: number;
//         forecasted_remaining: number;
//         forecasted_total: number;
//     }[];
//     credits: number;
// }

export interface BillingServiceItem {
    service: string;
    os_resource_id: string;
    actual_cost_so_far: number;
    current_hourly_rate: number;
    forecasted_remaining: number;
    forecasted_total: number;
    hours_active: number;
    metadata: any;
    is_active: boolean;
    recorded_at: string;
    ended_at: string | null;
}

export interface BillingForecast {
    policy_version?: number;
    paymentRecovery?: import('./paymentSlice').PaymentRecovery | null;
    summary: {
        actual_spent_this_month: number;
        forecasted_total_this_month: number;
        forecasted_remaining: number;
        subscription_fee?: number;
        paid_usage?: number;
    };
    active: BillingServiceItem[];
    inactive: BillingServiceItem[];
    credits: number;
    credits_for_period?: number | null;
    credits_source?: 'stripe' | 'trial' | 'ledger' | 'plan_ledger';

    credits_expires_at?: number | null;
    credits_expiry_soon?: boolean;
    billing_period?: {
        start: string;
        end: string;
        source: 'stripe' | 'trial' | 'plan_ledger';
    };
    tier?: string | null;
    status?: string | null;
    /**
     * The welcome credit, in GBP, a no-card trial receives when it adds a card (the server's CARDED_WELCOME_CREDIT_GBP),
     * which the trial-ended banner names. In this read because this read decides that banner, so the state and the
     * amount always arrive together. 0: there is none, and the banner promises none. null or absent: not known, and the
     * banner promises none either.
     */
    welcome_credit_gbp?: number | null;
    // status = 'suspended' is written both by the billing recorder (trial credits exhausted) and by
    // an admin suspension. Only this field separates them, and the two need opposite messages.
    suspended_by_admin?: boolean;
    trial_expires_at?: string | null;
    free_entitlement?: boolean;
    free_lifecycle?: {suspended_at:string|null;delete_after?:string|null};
    egress_usage?: {
        total_bytes: string;
        chargeable_bytes: string;
        free_tier_bytes: string;
    } | null;
}

/**
 * Whether this account must have a monthly budget, and whether it has one that works (BUDGET_MANDATORY_PLAN_2026-10-28
 * sections 5.1 and 5.6): the fields of GET /v1/billing/budget that the budget banner (TrialBanner) reads.
 */
export interface BudgetState {
    /** A working card account, not internal and not an operator's: it must have a budget. */
    required: boolean;
    /** A budget is saved and armed: once this month's spend reaches it, new resources are refused. */
    active: boolean;
    /** A cap saved before budgets blocked anything: stored, but it blocks nothing. The banner asks to confirm it. */
    legacy: boolean;
    limit: number | null;
    /**
     * What the saved budget does once reached (utility/budget.ts): soft refuses new resources, hard also deletes every
     * billable resource. A removed budget, a legacy cap and every budget saved before the modes read soft.
     */
    mode: BudgetMode;
}

interface BillingState {
    forecast: BillingForecast | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    budget: BudgetState | null;
    budgetStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: BillingState = {
    forecast: null,
    status: 'idle',
    error: null,
    budget: null,
    budgetStatus: 'idle',
};

export const fetchBillingForecast = createAsyncThunk(
    'billing/fetchBillingForecast',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/v1/billing/forecast');
            return response.data as BillingForecast;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

/*
 * ⛔ The budget state, read once at login next to the forecast (App.tsx), from GET /v1/billing/budget, which reads only
 * the database. Never from the forecast: for card accounts that calls the payment provider and fails with it
 * (BUDGET_MANDATORY_PLAN_2026-10-28 section 5.2). A 404 (no billing row yet) or any other error resolves to null, which
 * shows no banner: it fails quietly, never with a false banner. So it never rejects: the store's listener toasts every
 * rejected fetch (store.ts), and nothing here is a failure the person can act on. Only a literal true counts, so an API
 * without these fields shows no banner either, and only a literal "hard" is a hard budget.
 */
export const fetchBudgetState = createAsyncThunk(
    'billing/fetchBudgetState',
    async (): Promise<BudgetState | null> => {
        try {
            const { data } = await axiosInstance.get('/v1/billing/budget');
            return {
                required: data?.required === true,
                active: data?.active === true,
                legacy: data?.legacy === true,
                limit: typeof data?.limit === 'number' ? data.limit : null,
                mode: budgetModeOf(data?.mode),
            };
        } catch {
            return null;
        }
    }
);

const billingSlice = createSlice({
    name: 'billing',
    initialState,
    reducers: {
        /*
         * A budget saved in BudgetSettings, from the PUT reply ({ limit, enforcement_active, mode, ... }), so the budget
         * banner goes without a reload (plan section 5.6). That reply does not carry required, and a save does not change
         * it. Its mode is the mode the server saved, which is the one to show, whatever was asked for.
         */
        budgetSaved: (state, action: PayloadAction<{ limit: number | null; enforcement_active?: boolean; mode?: string }>) => {
            if (!state.budget) return;
            const { limit, enforcement_active, mode } = action.payload;
            const capped = limit !== null && limit >= 0;
            state.budget.limit = limit;
            state.budget.active = capped && enforcement_active === true;
            state.budget.legacy = capped && enforcement_active !== true;
            state.budget.mode = budgetModeOf(mode);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBudgetState.pending, (state) => {
                state.budgetStatus = 'loading';
            })
            .addCase(fetchBudgetState.fulfilled, (state, action) => {
                state.budget = action.payload;
                state.budgetStatus = action.payload ? 'succeeded' : 'failed';
            })
            .addCase(fetchBillingForecast.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchBillingForecast.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.forecast = action.payload;
            })
            .addCase(fetchBillingForecast.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { budgetSaved } = billingSlice.actions;
export default billingSlice.reducer;
